import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, HardHat, DollarSign, Users, Warehouse, BookOpen, BarChart3, ShieldCheck, Check } from 'lucide-react';
import WebsiteNav from './WebsiteNav';
import WebsiteFooter from './WebsiteFooter';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const SECTIONS = [
  {
    id: 'planning', icon: LayoutDashboard, color: '#3B82F6', title: 'Planning Module',
    desc: 'Lay the groundwork for every project with structured planning tools.',
    img: '/screenshots/dashboard.jpg', imgAlt: 'Dashboard',
    features: ['Project management with lifecycle tracking', 'Work Breakdown Structure (WBS) hierarchy', 'Milestones with planned dates & task assignments', 'Team management by trade category', 'Priority levels, status tracking & subtasks'],
  },
  {
    id: 'execution', icon: HardHat, color: '#10B981', title: 'Execution Module',
    desc: 'Track daily operations, manpower, materials, and team productivity in real-time.',
    img: '/screenshots/teams.jpg', imgAlt: 'Execution',
    features: ['Daily site logs per team with progress tracking', 'Manpower tracking with auto man-hour calculations', 'Material tracking with running balance', 'Team reports that lock underlying data', 'Productivity analysis vs planned outputs'],
  },
  {
    id: 'finance', icon: DollarSign, color: '#F59E0B', title: 'Finance Module',
    desc: 'Complete financial control from budgets to invoices, payments, and change orders.',
    img: '/screenshots/financial-dashboard.jpg', imgAlt: 'Financial Dashboard',
    features: ['Activity-level budgeting with unit costs', 'Auto cost derivation from execution data', 'Budget vs Actual variance analysis', 'Invoicing with VAT/tax calculations', 'Payments, variations & financial dashboard'],
  },
  {
    id: 'payroll', icon: Users, color: '#8B5CF6', title: 'Payroll & HR',
    desc: 'Employee management, salary processing, and UAE-compliant End of Service calculations.',
    img: '/screenshots/payroll.jpg', imgAlt: 'Payroll',
    features: ['Employee profiles with salary structures', 'Configurable allowances & deductions', 'Monthly payroll runs with auto-calculation', 'Payslip generation & EOS benefits', 'Auto journal entries for accounting integration'],
  },
  {
    id: 'inventory', icon: Warehouse, color: '#06B6D4', title: 'Inventory Management',
    desc: 'Full stock control with warehouses, suppliers, purchase orders, and movements.',
    img: '/screenshots/inventory.jpg', imgAlt: 'Inventory',
    features: ['Multiple warehouse management', 'Supplier database with payment terms', 'Purchase order creation & tracking', 'Stock in/out/transfer movements', 'Auto stock level updates & cost integration'],
  },
  {
    id: 'accounting', icon: BookOpen, color: '#EC4899', title: 'Accounting & Ledgers',
    desc: 'Double-entry bookkeeping with full financial statements and compliance reporting.',
    img: '/screenshots/accounting.jpg', imgAlt: 'Accounting',
    features: ['Configurable Chart of Accounts', 'General Ledger with transaction history', 'Balanced journal entries (auto & manual)', 'Trial Balance, P&L, and Balance Sheet', 'VAT report generation for compliance'],
  },
  {
    id: 'reports', icon: BarChart3, color: '#F97316', title: 'Reports & Analytics',
    desc: 'Auto-generated reports for executives, project managers, and finance teams.',
    img: '/screenshots/reports.jpg', imgAlt: 'Reports',
    features: ['Weekly Technical, Operational & Financial reports', 'Executive dashboard with portfolio KPIs', 'Project-specific performance dashboards', 'Advanced: Profitability, utilization & valuation', 'PDF and Excel export capabilities'],
  },
  {
    id: 'admin', icon: ShieldCheck, color: '#6366F1', title: 'Administration & Security',
    desc: 'Enterprise-grade access control, multi-tenant isolation, and audit trails.',
    img: '/screenshots/role-permissions.jpg', imgAlt: 'Role Permissions',
    features: ['11 configurable roles with editable matrix', 'User invitation system with shareable links', 'Multi-tenant architecture with data isolation', 'Subscription management per tenant', 'Complete audit trail of all actions'],
  },
];

export default function FeaturesPage() {
  return (
    <div className="bg-[#0B0F19] min-h-screen text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <WebsiteNav />

      {/* HERO */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.25em] uppercase text-blue-400 mb-4">Features</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built for Construction
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 mt-4 max-w-2xl mx-auto text-lg">
              Eight powerful modules covering every aspect of project management, from planning through financial close-out.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FEATURE SECTIONS */}
      {SECTIONS.map((s, idx) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-20 md:py-28 ${idx % 2 === 0 ? '' : 'bg-[#0E1220]'}`}
          data-testid={`feature-section-${s.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${idx % 2 === 0 ? '' : 'lg:[direction:rtl]'}`}>
              {/* Text */}
              <motion.div
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
                className="lg:[direction:ltr]"
              >
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.color + '18' }}>
                    <s.icon size={20} style={{ color: s.color }} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h2>
                </motion.div>
                <motion.p variants={fadeUp} className="text-slate-400 mb-6 leading-relaxed">{s.desc}</motion.p>
                <motion.ul variants={stagger} className="space-y-3">
                  {s.features.map((f, fi) => (
                    <motion.li key={fi} variants={fadeUp} className="flex items-start gap-2.5">
                      <Check size={16} className="mt-0.5 shrink-0" style={{ color: s.color }} />
                      <span className="text-sm text-slate-300">{f}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>

              {/* Screenshot */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="lg:[direction:ltr]"
              >
                <div className="bg-[#1A1F36] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                  <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#141827] border-b border-white/5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    <span className="text-[10px] text-slate-600 ml-2">ProjectBook Pro&trade;</span>
                  </div>
                  <img src={s.img} alt={s.imgAlt} className="w-full" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 bg-[#0E1220] relative overflow-hidden">
        <div className="absolute top-10 left-1/4 w-72 h-72 bg-blue-500/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            See It Live
          </h2>
          <p className="text-slate-400 mb-8">Experience all features with a free demo account.</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/demo" className="px-8 py-3.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-lg transition-all text-sm shadow-lg shadow-blue-500/25" data-testid="features-cta-btn">
              Try Live Demo
            </Link>
            <a href="mailto:info@sgkapps.com" className="px-8 py-3.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-lg transition-all text-sm">
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
